/* eslint-disable react/prop-types */
import React from 'react';
import Highcharts from 'highcharts';
import lodashMerge from 'lodash/merge';
import { chartConfigUtilService } from './chartConfigUtilService';
import { GenericChart } from './HighchartsGeneric';

function yAxisLabelFormatter(this: Highcharts.AxisLabelsFormatterContextObject): string {
    const value = this.value as number;
    if (value >= 1000000) {
        return `${value / 1000000} M`;
    }
    if (value >= 1000) {
        return `${value / 1000}k`;
    }
    return String(value);
}

function formatValue(value: number): string {
    if (value >= 1000000) {
        return `${(value / 1000000).toFixed(0)}M`;
    }
    if (value >= 1000) {
        return `${(value / 1000).toFixed(0)}K`;
    }
    return String(value);
}

function tooltipPointFormatter(this: Highcharts.Point): string {
    return `<div><span style="color: ${this.color};">${this.series.name}:</span> <b>${formatValue(
        this.y as number,
    )}</b></div>`;
}

export interface ColumnChartSeriesItem {
    name: string;
    data: number[];
    color: string;
}

export interface ColumnChartProps {
    series: ColumnChartSeriesItem[];
    categories: string[];
    height?: number;
    yAxisTitle?: string;
    showLegend?: boolean;
    additionalConfig?: Highcharts.Options;
}

export const ColumnChart: React.FC<ColumnChartProps> = ({
    series,
    categories,
    height = 300,
    yAxisTitle = '',
    showLegend = true,
    additionalConfig = {},
}) => {
    if (series.length === 0) return null;

    const seriesData: Highcharts.SeriesOptionsType[] = series.map((s) => ({
        type: 'column' as const,
        name: s.name,
        data: s.data,
        color: s.color,
    }));

    const chartConfig = lodashMerge(
        {},
        chartConfigUtilService.BASE_CHART_CONFIG,
        {
            chart: {
                type: 'column',
                height,
                spacing: [10, 20, 10, 20],
            },
            xAxis: {
                categories,
                crosshair: true,
                labels: {
                    style: {
                        fontSize: '12px',
                        fontWeight: '300',
                        color: 'rgba(98, 100, 105, 1)',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        /*
                         * `width` is what makes textOverflow:'ellipsis' truncate: a uniform max
                         * width ellipsizes every label (Highcharts adds the full-name hover
                         * tooltip). Without it, only edge labels get cut via overflow:'justify'.
                         */
                        width: 120,
                    },
                },
            },
            yAxis: {
                min: 0,
                title: {
                    text: yAxisTitle,
                },
                labels: {
                    formatter: yAxisLabelFormatter,
                },
            },
            legend: {
                enabled: showLegend,
                align: 'center',
                verticalAlign: 'top',
                layout: 'horizontal',
                symbolRadius: 2,
                itemStyle: {
                    fontWeight: '400',
                    fontSize: '12px',
                },
            },
            tooltip: {
                shared: true,
                useHTML: true,
                shape: 'rect',
                padding: 10,
                outside: true, // This helps with clipping issues
                headerFormat: '<div style="font-size: 14px; font-weight: 500;">{point.key}</div>',
                pointFormatter: tooltipPointFormatter,
            },
            plotOptions: {
                series: {
                    pointPlacement: null,
                },
                column: {
                    pointWidth: 4,
                    pointPadding: 0.15,
                    borderWidth: 0,
                    borderRadius: 1,
                    groupPadding: 0,
                },
            },
            series: seriesData,
        },
        additionalConfig,
    );

    return <GenericChart config={chartConfig} />;
};

export default ColumnChart;
